// src/pages/Coleta/ColetaPage.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    Grid,
    IconButton,
    MenuItem,
    Paper,
    Skeleton,
    Stack,
    TextField,
    Tooltip,
    Typography,
    alpha,
} from '@mui/material';
import * as Lucide from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import FiltrosPanel, { CAMPO } from '../../components/FiltrosPanel';
import ConfirmationDialog from '../../components/ConfirmationDialog';
import { SnackbarAlert } from '../../components/SnackbarAlert';
import { useBootstrap } from '../../context/BootstrapContext';
import useExperimentosAtivos from '../../hooks/useExperimentosAtivos';
import useSessionState from '../../hooks/useSessionState';
import { usePermissao } from '../../security/usePermissao';
import { processarExperimento } from '../../infra/api/ExperimentoApi';
import {
    buscarColetas,
    cancelarColeta,
    experimentosSemRegistro,
    listarPaginasColeta,
    listarPontosColeta,
    mensagemErro,
    origensColeta,
    referenciasPagina,
    salvarReferenciasPagina,
    serieColetas,
} from '../../infra/api/Coletas';
import {
    PAGINAS_COLETA,
    PERIODOS,
    TURNOS,
    periodoEmDatas,
    rotuloAlvo,
    unidadeExibida,
} from '../../utils/coleta';
import ColetaDialog from './components/ColetaDialog';
import ColetasAtivas from './components/ColetasAtivas';
import ConfigurarPaginaDialog from './components/ConfigurarPaginaDialog';
import EvolucaoChart from './components/EvolucaoChart';
import HistoricoColetas from './components/HistoricoColetas';
import HistoricoQualitativo from './components/HistoricoQualitativo';
import VerificacoesPagina from './components/VerificacoesPagina';
import { MARGEM_LATERAL, TITULO_PAGINA } from '../../theme/escalaPagina';

/** O histórico vem paginado do servidor; nunca a lista inteira. */
const TAMANHOS_PAGINA = [5, 10, 20];
const FILTROS_INICIAIS = Object.freeze({ periodo: 14, turno: '', pontoColetaId: '', origem: '' });
/** Recarga silenciosa enquanto há ensaio rodando: o fim do ensaio não chega pelo WebSocket. */
const RECARGA_AO_VIVO_MS = 60_000;

/**
 * Página de coleta do plano LEITE (`/leite`, `/colostro`, `/agua`). Cada uma é presa a
 * UMA análise de protocolo, escolhida pelo ADMIN. O registro da amostra fica aguardando
 * o tubo; quando ele entra no dispositivo, o experimento nasce ligado ao registro e o
 * resultado do Processar vira a contagem da página.
 */
export default function ColetaPage({ pagina }) {
    const meta = PAGINAS_COLETA[pagina];
    const Icone = Lucide[meta.icone];
    const { tenant } = useParams();
    const navigate = useNavigate();
    const abrir = (linha) => navigate(`/${tenant}/${meta.rota}/${linha.id}`);
    const { bootstrap } = useBootstrap();
    const { pode } = usePermissao();
    const podeAlterar = pode('dados.alterar');
    const podeProcessar = podeAlterar && pode('amostra.processarIA');
    const podeConfigurar = pode('coleta.configurarPagina');

    // ── configuração da página ──
    const [config, setConfig] = useState(null);
    const [carregandoConfig, setCarregandoConfig] = useState(true);
    const [configAberta, setConfigAberta] = useState(false);

    useEffect(() => {
        let ativo = true;
        setCarregandoConfig(true);
        listarPaginasColeta()
            .then((lista) => ativo && setConfig((lista || []).find((c) => c.pagina === pagina) || null))
            .catch(() => ativo && setConfig(null))
            .finally(() => ativo && setCarregandoConfig(false));
        return () => {
            ativo = false;
        };
    }, [pagina]);

    // ── referências do gráfico (legislação + metas): as mesmas das regras do assistente ──
    const [referencias, setReferencias] = useState(null);
    useEffect(() => {
        if (meta.qualitativo) return undefined;
        let ativo = true;
        setReferencias(null);
        referenciasPagina(pagina)
            .then((r) => ativo && setReferencias(r))
            .catch(() => ativo && setReferencias({ erro: true, itens: [] }));
        return () => {
            ativo = false;
        };
    }, [pagina, meta.qualitativo]);
    const salvarReferencias = async (payload) => {
        const salvo = await salvarReferenciasPagina(pagina, payload);
        setReferencias(salvo);
        return salvo;
    };

    const protocolo = useMemo(
        () => (bootstrap?.protocolos || []).find((p) => p.id === config?.protocoloId),
        [bootstrap?.protocolos, config?.protocoloId]
    );
    const unidade = unidadeExibida(protocolo, config?.unidade);

    const rotuloMedida = rotuloAlvo(config?.alvo);

    // ── filtros: rascunho no painel, aplicados ao buscar ──
    // Filtros aplicados e página ficam na sessão: voltar do detalhe de uma amostra não
    // pode desfazer a busca de quem estava no meio do histórico.
    const [filtros, setFiltros] = useSessionState(`coletaPage_${pagina}_filtros`, FILTROS_INICIAIS);
    const [page, setPage] = useSessionState(`coletaPage_${pagina}_pagina`, 0);
    const [tamanho, setTamanho] = useSessionState(`coletaPage_${pagina}_tamanho`, TAMANHOS_PAGINA[1]);
    const [rascunho, setRascunho] = useState(filtros);
    // Eixo X do gráfico = o período do filtro inteiro, não só os dias com contagem.
    const periodoGrafico = useMemo(() => periodoEmDatas(filtros.periodo), [filtros.periodo]);
    const filtrosAtivos = Object.keys(FILTROS_INICIAIS).filter((k) => filtros[k] !== FILTROS_INICIAIS[k]).length;

    // ── dados ──
    const [pontos, setPontos] = useState([]);
    const [origens, setOrigens] = useState([]);
    const [resultado, setResultado] = useState({ items: [], totalElements: 0, totalPages: 0 });
    const [ativas, setAtivas] = useState([]);
    const [semRegistro, setSemRegistro] = useState([]);
    const [serie, setSerie] = useState([]);
    const [carregando, setCarregando] = useState(false);

    const [processando, setProcessando] = useState(() => new Set());
    const [dialogo, setDialogo] = useState(null); // { modo, linha?, experimento? }
    // Atalho do assistente ("Registrar verificação"): /leite?registrar=1 abre a nova análise.
    // Com &ponto=<id> (cartão "Nova análise" dos Pontos de coleta) o ponto já vem escolhido.
    const [params, setParams] = useSearchParams();
    useEffect(() => {
        if (!params.get('registrar') || !config) return;
        if (podeAlterar) setDialogo({ modo: 'novo', pontoInicial: params.get('ponto') || null });
        const proximos = new URLSearchParams(params);
        proximos.delete('registrar');
        proximos.delete('ponto');
        setParams(proximos, { replace: true });
    }, [params, setParams, config, podeAlterar]);
    const [paraCancelar, setParaCancelar] = useState(null);
    const [aviso, setAviso] = useState({ open: false, message: '', severity: 'info' });
    const avisar = (severity, message) => setAviso({ open: true, severity, message });

    const carregar = useCallback(
        async ({ silencioso = false } = {}) => {
            if (!config) return;
            if (!silencioso) setCarregando(true);
            // Pagina qualitativa (agua): coletas esparsas, sem periodo — o historico inteiro,
            // paginado. As demais seguem o periodo do filtro (e o grafico).
            const { de, ate } = meta.qualitativo ? { de: null, ate: null } : periodoEmDatas(filtros.periodo);
            try {
                const [lista, emCurso, semReg, pontosSerie, todosPontos, origensPagina] = await Promise.all([
                    buscarColetas(
                        {
                            pagina,
                            de,
                            ate,
                            turno: filtros.turno || null,
                            pontoColetaId: filtros.pontoColetaId || null,
                            origem: filtros.origem || null,
                        },
                        page,
                        tamanho
                    ),
                    buscarColetas({ pagina, status: ['PENDENTE', 'EM_PROCESSAMENTO'] }, 0, 20),
                    experimentosSemRegistro(pagina),
                    meta.qualitativo ? Promise.resolve([]) : serieColetas(pagina, de, ate),
                    listarPontosColeta({ incluirInativos: true }),
                    origensColeta(pagina),
                ]);
                setResultado(lista);
                setAtivas(emCurso.items);
                setSemRegistro(semReg);
                setSerie(pontosSerie);
                setPontos(todosPontos);
                setOrigens(origensPagina);
            } catch (e) {
                // A recarga de fundo não deve assustar o operador com um aviso a cada minuto.
                if (!silencioso) avisar('error', mensagemErro(e, 'Não foi possível carregar as amostras.'));
            } finally {
                if (!silencioso) setCarregando(false);
            }
        },
        [config, pagina, filtros, page, tamanho, meta.qualitativo]
    );

    useEffect(() => {
        carregar();
    }, [carregar]);

    // ── tempo real: só enquanto há algo acontecendo ──
    const aoVivo = ativas.length > 0 || semRegistro.some((e) => String(e.status).toLowerCase() === 'active');
    const { experimentoPorSerial } = useExperimentosAtivos({ enabled: aoVivo });
    const carregarRef = useRef(carregar);
    carregarRef.current = carregar;

    // Um dispositivo iniciou ou parou: recarrega (debounce para tubos inseridos em sequência).
    const mapaAnteriorRef = useRef(null);
    useEffect(() => {
        const mudou = mapaAnteriorRef.current !== null && mapaAnteriorRef.current !== experimentoPorSerial;
        mapaAnteriorRef.current = experimentoPorSerial;
        if (!mudou || !aoVivo) return undefined;
        const t = setTimeout(() => carregarRef.current({ silencioso: true }), 2000);
        return () => clearTimeout(t);
    }, [experimentoPorSerial, aoVivo]);

    useEffect(() => {
        if (!aoVivo) return undefined;
        const id = setInterval(() => carregarRef.current({ silencioso: true }), RECARGA_AO_VIVO_MS);
        return () => clearInterval(id);
    }, [aoVivo]);

    // ── pontos: todos para nomear, os da análise para escolher ──
    const nomePonto = useCallback((id) => pontos.find((p) => p.id === id)?.nome || null, [pontos]);
    const pontosDaAnalise = useMemo(
        () =>
            pontos.filter(
                (p) =>
                    p.ativo !== false &&
                    (!p.analiseIds?.length || p.analiseIds.includes(config?.analiseId))
            ),
        [pontos, config?.analiseId]
    );

    // ── ações ──
    const processar = async (linha) => {
        const id = linha.experimento?.id;
        if (!id) return;
        setProcessando((atual) => new Set(atual).add(id));
        try {
            await processarExperimento(id);
            avisar('success', `${linha.identificacao} processada.`);
            await carregar({ silencioso: true });
        } catch (e) {
            avisar('error', mensagemErro(e, 'Não foi possível processar a amostra.'));
        } finally {
            setProcessando((atual) => {
                const proximo = new Set(atual);
                proximo.delete(id);
                return proximo;
            });
        }
    };

    const confirmarCancelamento = async () => {
        const linha = paraCancelar;
        setParaCancelar(null);
        try {
            await cancelarColeta(linha.id);
            avisar('success', `Registro ${linha.identificacao} cancelado.`);
            await carregar({ silencioso: true });
        } catch (e) {
            avisar('error', mensagemErro(e, 'Não foi possível cancelar o registro.'));
        }
    };

    const aoSalvar = (registro) => {
        const modo = dialogo?.modo;
        setDialogo(null);
        avisar(
            'success',
            modo === 'novo'
                ? `${registro.identificacao} registrada. Insira o tubo em ${registro.numeroSerie || 'no dispositivo'}.`
                : `${registro.identificacao} salva.`
        );
        carregar({ silencioso: true });
    };

    const buscar = () => {
        setPage(0);
        setFiltros(rascunho);
    };
    const limpar = () => {
        setRascunho(FILTROS_INICIAIS);
        setFiltros(FILTROS_INICIAIS);
        setPage(0);
    };

    const campo = (chave) => ({
        value: rascunho[chave],
        onChange: (e) => setRascunho((r) => ({ ...r, [chave]: e.target.value })),
    });

    return (
        <Box sx={{ px: MARGEM_LATERAL, pt: { xs: 2, sm: 2.5 }, pb: { xs: 2, sm: 3, md: 4 } }}>
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'stretch', sm: 'flex-start' }}
                spacing={2}
                sx={{ mb: 3 }}
            >
                <Box sx={{ minWidth: 0 }}>
                    <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1.2, fontWeight: 700 }}>
                        Controle microbiológico
                    </Typography>
                    <PageHeader title={meta.titulo} subtitle={meta.subtitulo} titleSx={TITULO_PAGINA} />
                </Box>
                {config && (
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
                        {podeConfigurar && (
                            <Tooltip title="Configurar página">
                                <IconButton onClick={() => setConfigAberta(true)} aria-label="Configurar página">
                                    <Lucide.Settings2 size={20} />
                                </IconButton>
                            </Tooltip>
                        )}
                        {podeAlterar && (
                            <Button
                                variant="contained"
                                disableElevation
                                startIcon={<Lucide.Plus size={18} />}
                                onClick={() => setDialogo({ modo: 'novo' })}
                                sx={{ borderRadius: 2, flex: { xs: 1, sm: 'none' } }}
                            >
                                Nova análise
                            </Button>
                        )}
                    </Stack>
                )}
            </Stack>

            {carregandoConfig ? (
                <Stack spacing={2}>
                    <Skeleton variant="rounded" height={56} />
                    <Skeleton variant="rounded" height={320} />
                </Stack>
            ) : !config ? (
                <Paper variant="outlined" sx={{ p: { xs: 3, sm: 5 }, borderRadius: 3, textAlign: 'center' }}>
                    <Box sx={{ color: 'text.secondary', mb: 1.5 }}>
                        <Lucide.Settings2 size={32} />
                    </Box>
                    <Typography fontWeight={700} sx={{ mb: 0.5 }}>Esta página ainda não foi configurada</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: podeConfigurar ? 2.5 : 0 }}>
                        {podeConfigurar
                            ? 'Escolha a análise do protocolo que esta página vai mostrar.'
                            : 'A equipe BioAILab está preparando esta página. Fale com o suporte se precisar dela agora.'}
                    </Typography>
                    {podeConfigurar && (
                        <Button variant="contained" disableElevation onClick={() => setConfigAberta(true)} sx={{ borderRadius: 2 }}>
                            Configurar página
                        </Button>
                    )}
                </Paper>
            ) : (
                <Stack spacing={3}>
                    {meta.qualitativo && (
                        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3 }}>
                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
                                <Box sx={{ p: 1.5, borderRadius: 2.5, bgcolor: alpha(meta.cor, 0.12), color: meta.cor, display: 'flex', alignSelf: 'flex-start' }}>
                                    <Icone size={26} />
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography fontWeight={700} sx={{ fontSize: '1.1rem' }}>Um ponto de coleta, um histórico</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        E. coli + coliformes totais, sempre no mesmo teste de água. Resultados qualitativos separados: detectado ou não detectado
                                        (ausência em 100 mL). Base normativa: Portaria GM/MS Nº 888/2021.
                                    </Typography>
                                </Box>
                                <TextField
                                    select
                                    size="small"
                                    value={filtros.pontoColetaId}
                                    onChange={(e) => {
                                        const pontoColetaId = e.target.value;
                                        setFiltros((f) => ({ ...f, pontoColetaId }));
                                        setRascunho((f) => ({ ...f, pontoColetaId }));
                                        setPage(0);
                                    }}
                                    label="Ponto de coleta"
                                    SelectProps={{ displayEmpty: true }}
                                    InputLabelProps={{ shrink: true }}
                                    sx={{ minWidth: { sm: 220 }, width: { xs: '100%', sm: 'auto' } }}
                                >
                                    <MenuItem value="">Todos os pontos</MenuItem>
                                    {pontosDaAnalise.map((p) => (
                                        <MenuItem key={p.id} value={p.id}>{p.nome}</MenuItem>
                                    ))}
                                </TextField>
                            </Stack>
                        </Paper>
                    )}
                    {!meta.qualitativo && (<>
                    <FiltrosPanel ativos={filtrosAtivos} loading={carregando} onLimpar={limpar} onBuscar={buscar}>
                        <Grid item {...CAMPO}>
                            <TextField select fullWidth size="small" label="Período" {...campo('periodo')}>
                                {PERIODOS.map((d) => (
                                    <MenuItem key={d} value={d}>Últimos {d} dias</MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item {...CAMPO}>
                            <TextField select fullWidth size="small" label="Ordenha" {...campo('turno')}>
                                <MenuItem value="">Todas as ordenhas</MenuItem>
                                {TURNOS.map((t) => (
                                    <MenuItem key={t.valor} value={t.valor}>{t.rotulo}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item {...CAMPO}>
                            <TextField select fullWidth size="small" label="Ponto de coleta" {...campo('pontoColetaId')}>
                                <MenuItem value="">Todos os pontos</MenuItem>
                                {pontosDaAnalise.map((p) => (
                                    <MenuItem key={p.id} value={p.id}>{p.nome}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item {...CAMPO}>
                            <TextField select fullWidth size="small" label="Origem" {...campo('origem')}>
                                <MenuItem value="">Todas as origens</MenuItem>
                                {origens.map((o) => (
                                    <MenuItem key={o} value={o}>{o}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                    </FiltrosPanel>

                    <EvolucaoChart
                        serie={serie}
                        pontos={pontos}
                        unidade={unidade}
                        rotuloMedida={rotuloMedida}
                        carregando={carregando && serie.length === 0}
                        periodo={periodoGrafico}
                        referencias={referencias}
                        podeEditarReferencias={pode('coleta.referencias')}
                        onSalvarReferencias={salvarReferencias}
                        onAbrirAmostra={(amostraId) => abrir({ id: amostraId })}
                        pontosDaAnalise={pontosDaAnalise}
                    />
                    </>)}

                    <ColetasAtivas
                        ativas={ativas}
                        semRegistro={semRegistro}
                        rotuloMedida={rotuloMedida}
                        nomePonto={nomePonto}
                        podeAlterar={podeAlterar}
                        onCancelar={setParaCancelar}
                        onRegistrarExperimento={(experimento) => setDialogo({ modo: 'experimento', experimento })}
                        onAbrir={abrir}
                        duracaoEstimadaHoras={meta.duracaoEstimadaHoras}
                    />

                    {meta.qualitativo ? (
                        <>
                            <HistoricoQualitativo
                                linhas={resultado.items}
                                total={resultado.totalElements}
                                page={page}
                                totalPages={resultado.totalPages}
                                onPageChange={setPage}
                                tamanho={tamanho}
                                tamanhos={TAMANHOS_PAGINA}
                                onTamanhoChange={(n) => {
                                    setTamanho(n);
                                    setPage(0);
                                }}
                                carregando={carregando}
                                alvos={meta.alvosQualitativos}
                                Icone={Icone}
                                cor={meta.cor}
                                nomePonto={nomePonto}
                                processando={processando}
                                podeProcessar={podeProcessar}
                                onProcessar={processar}
                                onAbrir={abrir}
                            />
                            <Alert severity="info" icon={<Lucide.Info size={20} />} sx={{ borderRadius: 2 }}>
                                Resultados de água podem complementar uma investigação do leite. Eles não estabelecem, sozinhos,
                                a origem de uma contaminação.
                            </Alert>
                            <VerificacoesPagina pagina={pagina} titulo="Verificações relacionadas à água" />
                        </>
                    ) : (
                    <HistoricoColetas
                        linhas={resultado.items}
                        total={resultado.totalElements}
                        page={page}
                        totalPages={resultado.totalPages}
                        onPageChange={setPage}
                        tamanho={tamanho}
                        tamanhos={TAMANHOS_PAGINA}
                        onTamanhoChange={(n) => {
                            setTamanho(n);
                            setPage(0);
                        }}
                        carregando={carregando}
                        unidade={unidade}
                        Icone={Icone}
                        nomePonto={nomePonto}
                        processando={processando}
                        podeAlterar={podeAlterar}
                        podeProcessar={podeProcessar}
                        onProcessar={processar}
                        onEditar={(linha) => setDialogo({ modo: 'editar', linha })}
                        onCancelar={setParaCancelar}
                        onAbrir={abrir}
                    />
                    )}
                </Stack>
            )}

            {config && (
                <ColetaDialog
                    open={Boolean(dialogo)}
                    modo={dialogo?.modo}
                    pagina={pagina}
                    config={config}
                    unidade={unidade}
                    pontos={pontosDaAnalise}
                    origens={origens}
                    experimento={dialogo?.experimento}
                    linha={dialogo?.linha}
                    pontoInicial={dialogo?.pontoInicial}
                    onClose={() => setDialogo(null)}
                    onSalvo={aoSalvar}
                />
            )}

            <ConfigurarPaginaDialog
                open={configAberta}
                pagina={pagina}
                config={config}
                onClose={() => setConfigAberta(false)}
                onSalvo={(salva) => {
                    setConfigAberta(false);
                    setConfig(salva);
                    avisar('success', 'Página configurada.');
                }}
            />

            <ConfirmationDialog
                open={Boolean(paraCancelar)}
                onClose={() => setParaCancelar(null)}
                onConfirm={confirmarCancelamento}
                title="Cancelar registro"
                message={`Cancelar o registro ${paraCancelar?.identificacao || ''}? O dispositivo fica livre para outra análise.`}
                confirmText="Cancelar registro"
                cancelText="Voltar"
            />

            <SnackbarAlert
                open={aviso.open}
                onClose={() => setAviso((a) => ({ ...a, open: false }))}
                severity={aviso.severity}
                message={aviso.message}
            />
        </Box>
    );
}
